import { uniq } from 'lodash';
import moment, { Moment } from 'moment';
import {
  Allocation,
  AllocationSourceType,
} from 'api/resource-management/types';
import { isNonWorkingDay } from 'components/organisms/timesheets/common/utils';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { defaultFormat } from 'utils/date-field/formatter';
import { floatUid } from 'utils/uid';
import { AllocationEvent } from '../event-types';
import { shiftDateIfOnNonWorkingDay } from './dates';
import {
  getMaximumEndDate,
  getMinimumStartDate,
} from './resources-summary/common';

const getEventsInADay = <T extends Allocation | AllocationEvent>(
  day: Moment,
  allocations: T[],
): T[] =>
  allocations.filter(
    ({ startDate: collidingStartDate, endDate: collidingEndDate }) => {
      return day.isBetween(
        moment(collidingStartDate),
        moment(collidingEndDate),
        'day',
        '[]',
      );
    },
  );
const getTotalMinutesFromAllocations = <T extends Allocation | AllocationEvent>(
  allocations: T[],
): number => allocations.reduce((total, event) => total + event.minutes, 0);

const getSourceTaskIds = (allocations: AllocationEvent[]) =>
  allocations.reduce(
    (acc, allocation) =>
      acc.concat((allocation.sources ?? []).map(({ sourceId }) => sourceId)),
    [] as number[],
  );

export const mergeAllocations = <T extends AllocationEvent>(
  allocations: T[],
  workingDaysConfig: WorkingDaysConfiguration,
): T[] => {
  const minimumStartDateFromAllocation = getMinimumStartDate(allocations);
  const maximumEndDateFromAllocation = getMaximumEndDate(allocations);
  let currentDay = moment(minimumStartDateFromAllocation);
  const mergedAllocation: T[] = [];

  let previousAllocationParams: {
    startDate: Moment;
    minutes: number;
    referenceAllocation: T;
    sourceTaskIds?: number[];
  } | null = null;

  while (currentDay.isSameOrBefore(maximumEndDateFromAllocation)) {
    if (!isNonWorkingDay(currentDay, workingDaysConfig)) {
      const eventsInADay = getEventsInADay(currentDay, allocations);
      const totalMinutesForDay = getTotalMinutesFromAllocations(eventsInADay);

      if (previousAllocationParams == null && eventsInADay.length > 0) {
        previousAllocationParams = {
          startDate: currentDay,
          minutes: totalMinutesForDay,
          referenceAllocation: eventsInADay[0],
          sourceTaskIds: getSourceTaskIds(eventsInADay),
        };
      } else if (
        previousAllocationParams != null &&
        totalMinutesForDay !== previousAllocationParams.minutes
      ) {
        mergedAllocation.push({
          ...previousAllocationParams.referenceAllocation,
          id: floatUid(),
          startDate: defaultFormat(
            shiftDateIfOnNonWorkingDay(
              previousAllocationParams.startDate,
              workingDaysConfig,
            ),
          ),
          endDate: defaultFormat(
            shiftDateIfOnNonWorkingDay(
              currentDay.subtract(1, 'day').endOf('day'),
              workingDaysConfig,
              false,
            ),
          ),
          minutes: previousAllocationParams.minutes,
          sources:
            previousAllocationParams.sourceTaskIds != null
              ? uniq(previousAllocationParams.sourceTaskIds).map((x) => ({
                  sourceType: AllocationSourceType.Task,
                  sourceId: x,
                }))
              : undefined,
        });
        if (eventsInADay.length > 0) {
          previousAllocationParams = {
            startDate: currentDay,
            minutes: totalMinutesForDay,
            referenceAllocation: eventsInADay[0],
            sourceTaskIds: getSourceTaskIds(eventsInADay),
          };
        } else {
          previousAllocationParams = null;
        }
      } else if (
        eventsInADay.length > 0 &&
        previousAllocationParams?.sourceTaskIds != null
      ) {
        previousAllocationParams.sourceTaskIds.push(
          ...getSourceTaskIds(eventsInADay),
        );
      }
    }
    currentDay = currentDay.add(1, 'day');
  }
  if (previousAllocationParams != null) {
    mergedAllocation.push({
      ...previousAllocationParams.referenceAllocation,
      id: floatUid(),
      startDate: defaultFormat(
        shiftDateIfOnNonWorkingDay(
          previousAllocationParams.startDate,
          workingDaysConfig,
        ),
      ),
      endDate: defaultFormat(
        shiftDateIfOnNonWorkingDay(
          maximumEndDateFromAllocation.endOf('day'),
          workingDaysConfig,
          false,
        ),
      ),
      minutes: previousAllocationParams.minutes,
      sources:
        previousAllocationParams.sourceTaskIds != null
          ? uniq(previousAllocationParams.sourceTaskIds).map((x) => ({
              sourceType: AllocationSourceType.Task,
              sourceId: x,
            }))
          : undefined,
    });
  }

  return mergedAllocation;
};
